import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { PaperPlaneTilt, Smiley } from 'phosphor-react';
import { Loader } from './Loader';
import { Switch } from './Switch';

interface ChatInputProps {
  messageInput: string;
  setMessageInput: (value: string) => void;
  isAiMode: boolean;
  isSendingMessage: boolean;
  onSendMessage: () => void;
  onModeChange: (ai: boolean) => void;
  onManualModeClick?: () => void;
  showTooltip?: boolean;
  tooltipMessage?: string;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  messageInput,
  setMessageInput,
  isAiMode,
  isSendingMessage,
  onSendMessage,
  onModeChange,
  onManualModeClick,
  showTooltip = false,
  tooltipMessage = '',
  placeholder = "Escribe un mensaje...",
  className = '',
  containerClassName = ''
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement)?.closest('button[data-emoji-button]')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Auto-resize textarea (same logic as OrderDetail)
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = '32px';
      
      // Calculate the new height
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(scrollHeight, 120);
      
      // Only update height if it's greater than min height
      if (newHeight > 32) {
        textarea.style.height = `${newHeight}px`;
      } else {
        textarea.style.height = '32px';
      }
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const textBefore = messageInput.substring(0, start);
    const textAfter = messageInput.substring(end);
    
    const newText = textBefore + emojiData.emoji + textAfter;
    setMessageInput(newText);
    
    // Trigger resize after emoji insertion
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = start + emojiData.emoji.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
        
        // Recalculate height
        textareaRef.current.style.height = '32px';
        const scrollHeight = textareaRef.current.scrollHeight;
        const newHeight = Math.min(scrollHeight, 120);
        if (newHeight > 32) {
          textareaRef.current.style.height = `${newHeight}px`;
        } else {
          textareaRef.current.style.height = '32px';
        }
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isAiMode) {
      e.preventDefault();
      onSendMessage();
    }
  };

  return (
    <div className={`flex gap-3 items-end flex-1 min-w-0 ${containerClassName}`}>
      {/* Toggle IA - Switch */}
      <div className="flex items-center flex-shrink-0 relative">
        {showTooltip && (
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-2 bg-black text-white text-xs rounded-lg whitespace-nowrap animate-fade-in z-50">
            {tooltipMessage}
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
          </div>
        )}
        <div className="flex items-center gap-2 pb-3">
          <Switch
            checked={isAiMode}
            onCheckedChange={onModeChange}
            aria-label={isAiMode ? 'IA activo' : 'Modo manual activo'}
          />
          <span className={`font-sans font-normal text-sm transition-opacity duration-base ease-smooth ${
            isAiMode ? 'text-foreground opacity-100' : 'text-muted-foreground opacity-50'
          }`}>
            Auto
          </span>
        </div>
      </div>
      
      {/* Input and Send Button */}
      <div className={`flex gap-3 items-end flex-1 relative min-w-0 ${className}`}>
        <div className="flex-1 relative min-w-0">
          <textarea
            ref={textareaRef}
            placeholder={isAiMode ? "Para escribir, desactiva el chat automático" : placeholder}
            value={messageInput}
            onChange={handleTextareaChange}
            onClick={() => {
              if (isAiMode && onManualModeClick) {
                onManualModeClick();
              }
            }}
            onKeyDown={handleKeyDown}
            readOnly={isAiMode}
            className={`w-full rounded-2xl px-4 py-2 pr-12 font-sans font-normal text-base outline-none resize-none overflow-y-hidden transition-all duration-base ease-smooth ${
              isAiMode ? 'placeholder:text-subtle' : 'placeholder:text-placeholder-text-passive'
            }`}
            style={{ 
              lineHeight: '1.4',
              height: '32px',
              maxHeight: '120px',
              overflow: 'hidden',
              backgroundColor: isAiMode ? 'rgb(var(--background))' : 'rgb(var(--background-passive))',
              color: isAiMode ? 'rgb(var(--muted-foreground))' : 'rgb(var(--passive-foreground))',
              cursor: isAiMode ? 'pointer' : 'text'
            }}
          />
          {/* Emoji Button - Only on desktop, inside input */}
          {!isMobile && !isAiMode && (
            <div className="absolute right-2 bottom-2" ref={emojiPickerRef}>
              <button
                data-emoji-button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="flex items-center justify-center rounded-full shrink-0 transition-opacity duration-base ease-smooth hover:bg-accent/5"
                style={{ width: '32px', height: '32px' }}
                type="button"
              >
                <Smiley weight="bold" size={20} className={isAiMode ? 'text-muted-foreground' : 'text-placeholder-text-passive'} style={{ width: '20px', height: '20px', display: 'block', flexShrink: 0 }} />
              </button>
              {showEmojiPicker && (
                <div className="absolute bottom-full right-0 mb-2 z-50">
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    autoFocusSearch={false}
                    skinTonesDisabled={true}
                    width={350}
                    height={400}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={onSendMessage}
          disabled={!messageInput.trim() || isAiMode || isSendingMessage}
          className="bg-primary border-none flex items-center justify-center rounded-full shrink-0 transition-opacity duration-base ease-smooth disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ width: '40px', height: '40px' }}
        >
          {isSendingMessage ? (
            <Loader />
          ) : (
            <PaperPlaneTilt weight="bold" size={20} className="text-primary-foreground" style={{ width: '20px', height: '20px', display: 'block', flexShrink: 0 }} />
          )}
        </button>
      </div>
    </div>
  );
};

